import { autoinject, computedFrom, PLATFORM, useView } from "aurelia-framework";
import { EntityTypes } from '@rightship-tech/common-ui/models/basic.models';
import { UserClaimsService } from '@rightship-tech/common-ui/services/basic/user-claims.service';
import { Claims } from '@rightship-tech/common-ui/constants/authentication/claims';
import { DueDiligenceClientModel, VetStatusEnum } from "../../../../../models/due-diligence.models";
import { UserClientService } from "@rightship-tech/common-ui/services/basic/user-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { DueDiligenceDialogConstants } from "../../../../../constants/dialog.constants";
import { DueDiligenceApi } from "../../../../../apis/due-diligence.api";
import { DueDiligenceClaims } from "../../../../../constants/claims.constants";

@autoinject
@useView(PLATFORM.moduleName("@rightship-tech/due-diligence-app/views/pages/due-diligence/tabs/message-center/due-diligence-message-center.html"))
export class DueDiligenceMessageCenter {

    private _dueDiligence: DueDiligenceClientModel;
    public messageCentre: any;
    public isRightShipUser: boolean = false;
    public task: { taskId: number, entityType: EntityTypes, emailIdentifier?: string, subtaskId?: number };

    @computedFrom("_dueDiligence", "_dueDiligence.vetStatusHistory", "_dueDiligence.vetStatusHistory.length", "_dueDiligence.vetStatusId")
    get lastVetStatusHistory() {
        if (this._dueDiligence.vetStatusHistory) {
            return  this._dueDiligence.vetStatusHistory.sort((a, b) => {
                return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
            })[0];
        }
        return null;
    }

    constructor(private userClaimsService: UserClaimsService, private userClientService: UserClientService, private dialogClientService: DialogClientService,
        private dueDiligenceApi: DueDiligenceApi) { }

    activate(params: { item: DueDiligenceClientModel, email: string, subtaskId: number }) {
        this.isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.task = {
            entityType: EntityTypes.Vet,
            taskId: params.item.vetId,
            emailIdentifier: params.email,
            subtaskId: params.subtaskId
        }
        this._dueDiligence = params.item;
    }

    @computedFrom("userClientService.currentUser.businessUnit.businessUnitId", "_dueDiligence.businessUnitId")
    get isCurrentUserWithinSameBusinessUnitAsDuediligence(): boolean {
        if (this._dueDiligence && this.userClientService.currentUser && this.userClientService.currentUser.businessUnit) {
            return this.userClientService.currentUser.businessUnit.businessUnitId == this._dueDiligence.businessUnitId;
        }
        return false;
    }

    @computedFrom("userClientService.currentUser.businessUnit.businessUnitId", "_dueDiligence.businessUnitId")
    public get canFulfilRfi(): boolean {
        this.userClientService?.currentUser.user.id
        return !this.isRightShipUser && this._dueDiligence.businessUnitId !== this.userClientService?.currentUser?.businessUnit?.businessUnitId;
    }

    @computedFrom("userClientService.currentUser.businessUnit.businessUnitId", "_dueDiligence.businessUnitId", "_dueDiligence.vetStatusId", "isRightShipUser")
    public get canShowAwaitingResponseButton(): boolean {
        return (this.isRightShipUser || this.isCurrentUserWithinSameBusinessUnitAsDuediligence) 
            && !(this._dueDiligence.vetStatusId == VetStatusEnum.DD_Unacceptable && this.userClaimsService.hasPermission(DueDiligenceClaims.DueDiligenceHideReviewButtonWhenUnacceptable))
            && this.userClaimsService.hasPermission(DueDiligenceClaims.CanReviewDueDiligence);
        }

    @computedFrom("userClientService.currentUser.businessUnit.businessUnitId", "_dueDiligence.businessUnitId", "_dueDiligence.vetStatusId", "isRightShipUser")
    public get canShowCompleteButton(): boolean {
        return (this.isRightShipUser || this.isCurrentUserWithinSameBusinessUnitAsDuediligence) 
            && !(this._dueDiligence.vetStatusId == VetStatusEnum.DD_Unacceptable && this.userClaimsService.hasPermission(DueDiligenceClaims.DueDiligenceHideReviewButtonWhenUnacceptable))
            && this.userClaimsService.hasPermission(DueDiligenceClaims.CanReviewDueDiligence)
            && this.userClaimsService.hasPermission(DueDiligenceClaims.CanMarkConversationAsAllReadOrCompleted);
        }

    //Handle status changing - ET1-1889
    public async handleStatusChanging($event) {
 
        //Check the statusId of the event
        if ($event && $event?.detail && $event?.detail?.statusId > 0)
        {
            const statusId = $event?.detail?.statusId;
            switch(statusId){
                //Awating response
                case 3:
                {
                    //Check if Due Diligence is not under review
                    if (this._dueDiligence.vetStatusId != VetStatusEnum.DD_UnderReview) {
                        
                        //Open dialog to set Due Diligence as awaiting response
                        this.dialogClientService.show(DueDiligenceDialogConstants.SetDueDiligenceAsAwaitingResponseDialog, 
                            { 
                                onContinue: async () => {
                                    await this.dueDiligenceApi.changeStatus(this.task.taskId, "", "", VetStatusEnum.DD_UnderReview);
                                    await this.messageCentre.setAsAwaitingResponse();
                                }
                            });
                    }
                    else {
                        await this.messageCentre.setAsAwaitingResponse();
                    }
                    break;
                }
                //Closed
                case 6: 
                {
                    //Check if Due Diligence is not under review
                    if (this._dueDiligence.vetStatusId != VetStatusEnum.DD_UnderReview) {

                        //Open dialog to mark thread as completed
                        this.dialogClientService.show(DueDiligenceDialogConstants.MarkThreadAsCompletedDialog, 
                            { 
                                onContinue: async () => {
                                    await this.dueDiligenceApi.changeStatus(this.task.taskId, "", "", VetStatusEnum.DD_UnderReview);
                                    await this.messageCentre.markThreadAsCompleted();
                                }
                            });
                    }
                    else {
                        await this.messageCentre.markThreadAsCompleted();
                    }
                    break;
                }
            }
        }
    }
}